import posthog from "posthog-js";

const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
const host = import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com";
const environment = import.meta.env.VITE_POSTHOG_ENVIRONMENT || "local";

/**
 * The one place the frontend's analytics environment tag is resolved. Exported
 * so no other module re-reads the Vite var and they can never disagree.
 *
 * MUST equal the backend's ANALYTICS_ENV (config/posthog.php) — the admin
 * Activity timeline filters events on $environment using the BACKEND value
 * (AP-3052), so a mismatch makes every frontend event invisible there.
 */
export const analyticsEnvironment = environment;

/**
 * Stamp $environment on every captured event as a super property.
 *
 * Registered rather than passed per-call because the per-call stamp in
 * events.js only covered trackEvent(): $pageview (usePageTracking.js) carried
 * no $environment at all, so the AP-3052 exact-match filter dropped every
 * pageview from the admin timeline — and an empty result there is
 * indistinguishable from "this customer never browsed".
 *
 * Called again after posthog.reset() (identify.js): reset() CLEARS super
 * properties, so without re-registering, every event captured after a logout
 * would lose the tag and disappear from the timeline all over again.
 */
export function registerEnvironment() {
    try {
        posthog.register({ $environment: environment });
    } catch {
        // Never break the app for analytics
    }
}

if (apiKey && environment !== "local") {
    posthog.init(apiKey, {
        api_host: host,
        autocapture: false,
        capture_pageview: false,    // We use Inertia router events instead
        capture_pageleave: false,
        capture_performance: false, // Disable web vitals
        disable_scroll_properties: true,
        persistence: "localStorage+cookie",
        session_recording: {
            maskAllInputs: true,
            maskTextSelector: "[data-ph-mask]",
            recordCrossOriginIframes: false,
            ...(environment === "production" ? { sampleRate: 0.1 } : {}),
        },
    });

    registerEnvironment();
}

export default posthog;
